import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { toPng, toJpeg } from 'html-to-image';
import { 
  Trophy, 
  Target, 
  Clock, 
  Users, 
  TrendingUp,
  BarChart3,
  Award,
  Star,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  Share,
  ArrowLeft,
  BookOpen,
  Zap,
  Calendar,
  Activity
} from 'lucide-react';
import TestAnalysisModal from './TestAnalysisModal';
import toast from 'react-hot-toast';

const EnhancedTestResults = ({ 
  results, 
  onBack, 
  onTakeAnotherTest, 
  onPracticeWeakAreas,
  testType = 'Mock Test' // 'Mock Test', 'Live Test', 'Practice Test'
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedSection, setSelectedSection] = useState('overall');
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('General');
  const [hoveredBar, setHoveredBar] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isCapturing, setIsCapturing] = useState(false);
  const resultsContainerRef = useRef(null);

  // Extract data from results with better fallbacks
  const {
    score: rawScore = 0,
    totalMarks: rawTotalMarks = 100,
    correctAnswers: rawCorrectAnswers = 0,
    wrongAnswers: rawWrongAnswers = 0,
    unattempted: rawUnattempted = 0,
    accuracy: rawAccuracy = 0,
    timeSpent: rawTimeSpent = 0,
    totalTime: resultsTotalTime,
    rank = 0,
    totalParticipants = 0,
    percentile = 0,
    sectionalResults = [],
    detailedResults = [],
    testName = '',
    completedAt = new Date(),
    cutOff = 55,
    testConfig = null, // Contains sections configuration
    // Real analytics data
    averageScore = 0,
    highestScore = 0,
    topPerformers = [],
    sectionalStats = []
  } = results || {};
  
  // Ensure all numeric values are properly converted to numbers
  const score = Number(rawScore) || 0;
  const totalMarks = Number(rawTotalMarks) || 100;
  const correctAnswers = Number(rawCorrectAnswers) || 0;
  const wrongAnswers = Number(rawWrongAnswers) || 0;
  const unattempted = Number(rawUnattempted) || 0;
  const accuracy = Number(rawAccuracy) || 0;
  const timeSpent = Number(rawTimeSpent) || 0;
  
  // Calculate totalTime properly - handle both minutes and seconds
  // If totalTime is > 3600 (1 hour), it's likely in seconds already
  // If totalTime is < 3600, it might be in minutes, so convert to seconds
  // Default to 60 seconds (1 minute) if not provided
  let totalTime = 60; // Default 1 minute in seconds
  if (resultsTotalTime !== undefined && resultsTotalTime !== null) {
    if (resultsTotalTime > 3600) {
      // Already in seconds (more than 1 hour)
      totalTime = resultsTotalTime;
    } else if (resultsTotalTime > 0 && resultsTotalTime <= 3600) {
      // Could be minutes or seconds - check if it makes sense as minutes
      // If it's a round number like 60, 120, 240, it's likely minutes
      // If it's 1, it's likely 1 minute = 60 seconds
      if (resultsTotalTime === 1) {
        totalTime = 60; // 1 minute = 60 seconds
      } else if (resultsTotalTime < 60 && resultsTotalTime % 1 === 0) {
        // Less than 60 and whole number - likely minutes
        totalTime = resultsTotalTime * 60;
      } else {
        // Already in seconds
        totalTime = resultsTotalTime;
      }
    }
  }

  // Calculate derived values with better validation
  const percentageScore = totalMarks > 0 ? Math.round((score / totalMarks) * 100) : 0;
  const attempted = correctAnswers + wrongAnswers;
  const totalQuestions = correctAnswers + wrongAnswers + unattempted;
  
  // Additional validation to ensure we have meaningful data
  const hasValidData = score > 0 || correctAnswers > 0 || wrongAnswers > 0 || totalQuestions > 0;

  // Ensure sectional results are always available
  const ensureSectionalResults = () => {
    // If sectional results already exist, use them as-is
    if (sectionalResults && sectionalResults.length > 0) {

      return sectionalResults;
    }

    // If test config has sections, create them from config
    if (testConfig && testConfig.sections && testConfig.sections.length > 0) {

      return testConfig.sections.map(section => {
        const sectionQuestions = results.questions?.filter((_, index) => 
          index >= section.startQuestion - 1 && index <= section.endQuestion - 1
        ) || [];
        
        const sectionCorrect = sectionQuestions.filter(q => q.isCorrect).length;
        const sectionAttempted = sectionQuestions.filter(q => {
          const userAnswer = q.userAnswer;
          const isAttempted = userAnswer !== null && 
                 userAnswer !== undefined && 
                 userAnswer !== '' && 
                 userAnswer !== 'null' && 
                 userAnswer !== 'undefined';

          return isAttempted;
        }).length;
        const sectionWrong = sectionAttempted - sectionCorrect;
        
        // Calculate section score properly accounting for negative marking
        // Use detailedResults if available for accurate score calculation
        let sectionScore = 0;
        if (results.detailedResults && Array.isArray(results.detailedResults)) {
          const sectionDetailedResults = results.detailedResults.filter((_, index) => 
            index >= section.startQuestion - 1 && index <= section.endQuestion - 1
          );
          sectionScore = sectionDetailedResults.reduce((sum, dr) => sum + (Number(dr.questionScore) || 0), 0);
        } else {
          // Fallback: calculate from correct/wrong counts using marking scheme
          const defaultMarkingScheme = { correct: 4, incorrect: -1, unattempted: 0 };
          sectionScore = (sectionCorrect * defaultMarkingScheme.correct) + (sectionWrong * defaultMarkingScheme.incorrect);
        }
        const marksPerQuestion = 4; // Default marking scheme
        const sectionUnattempted = sectionQuestions.length - sectionAttempted;
        const sectionAccuracy = sectionAttempted > 0 ? Math.round((sectionCorrect / sectionAttempted) * 100) : 0;
        
        // Calculate actual time spent on this section from detailedResults or questions
        let sectionTimeSpent = 0;
        if (results.detailedResults && Array.isArray(results.detailedResults)) {
          // Use actual time from detailedResults
          const sectionDetailedResults = results.detailedResults.filter((_, index) => 
            index >= section.startQuestion - 1 && index <= section.endQuestion - 1
          );
          sectionTimeSpent = sectionDetailedResults.reduce((sum, dr) => sum + (dr.timeSpent || 0), 0);
        } else if (results.questions && Array.isArray(results.questions)) {
          // Use actual time from questions array
          const sectionQuestionsWithTime = results.questions.filter((_, index) => 
            index >= section.startQuestion - 1 && index <= section.endQuestion - 1
          );
          sectionTimeSpent = sectionQuestionsWithTime.reduce((sum, q) => sum + (q.timeSpent || 0), 0);
        } else {
          // Fallback: Estimate time spent on this section proportionally
          const totalTimeSpent = results.totalTimeSpent || timeSpent || 0;
          const questionsCount = results.questions?.length || totalQuestions;
          sectionTimeSpent = questionsCount > 0 ? Math.floor(totalTimeSpent * (sectionQuestions.length / questionsCount)) : 0;
        }
        
        return {
          id: section.id,
          name: section.name,
          type: section.type,
          score: sectionScore,
          totalMarks: section.totalMarks || (sectionQuestions.length * marksPerQuestion),
          correctAnswers: sectionCorrect,
          wrongAnswers: sectionWrong,
          unattempted: sectionUnattempted,
          attempted: sectionAttempted,
          totalQuestions: sectionQuestions.length,
          accuracy: sectionAccuracy,
          timeSpent: sectionTimeSpent,
          totalTime: (section.timeLimit || (sectionQuestions.length * 60)) * 60, // Convert minutes to seconds
          startQuestion: section.startQuestion,
          endQuestion: section.endQuestion
        };
      });
    }

    // If no sections are defined, return empty array - don't generate defaults
    return [];
  };

  const finalSectionalResults = ensureSectionalResults();

  // Generate dynamic tabs and sections based on test configuration
  const generateDynamicTabs = () => {
    const tabs = [
      { id: 'overview', name: 'Overall', icon: BarChart3 }
    ];
    
    // Use finalSectionalResults if available (from actual test data)
    if (finalSectionalResults && finalSectionalResults.length > 0) {
      finalSectionalResults.forEach(section => {
        const iconMap = {
          'english': BookOpen,
          'numerical': TrendingUp,
          'reasoning': Zap,
          'general': Target,
          'custom': BookOpen
        };
        const tabId = section.name.toLowerCase().replace(/\s+/g, '-');
        tabs.push({
          id: tabId,
          name: section.name,
          icon: iconMap[section.type] || BookOpen
        });
      });
    } else if (testConfig && testConfig.sections) {
      testConfig.sections.forEach(section => {
        const iconMap = {
          'english': BookOpen,
          'numerical': TrendingUp,
          'reasoning': Zap,
          'general': Target,
          'custom': BookOpen
        };
        tabs.push({
          id: section.id,
          name: section.name,
          icon: iconMap[section.type] || BookOpen
        });
      });
    } else {
      // No sectional data available for tabs
    }
    
    return tabs;
  };

  const generateDynamicSections = () => {
    const sections = [
      { id: 'overall', name: 'Overall' }
    ];
    
    // Use finalSectionalResults if available (from actual test data)
    if (finalSectionalResults && finalSectionalResults.length > 0) {
      finalSectionalResults.forEach(section => {
        sections.push({
          id: section.name.toLowerCase().replace(/\s+/g, '-'),
          name: section.name
        });
      });
    } else if (testConfig && testConfig.sections) {
      testConfig.sections.forEach(section => {
        sections.push({
          id: section.id,
          name: section.name
        });
      });
    }
    
    return sections;
  };

  const tabs = generateDynamicTabs();
  const sections = generateDynamicSections();

  // Function to get current section data based on active tab
  const getCurrentSectionData = () => {
    if (activeTab === 'overview') {
      // Ensure score is a number and not NaN
      const numericScore = Number(score) || 0;
      return {
        score: numericScore,
        totalMarks: Number(totalMarks) || 100,
        correctAnswers: Number(correctAnswers) || 0,
        wrongAnswers: Number(wrongAnswers) || 0,
        unattempted: Number(unattempted) || 0,
        accuracy: Number(accuracy) || 0,
        timeSpent: Number(timeSpent) || 0,
        totalTime: totalTime, // Already converted to seconds
        attempted: Number(attempted) || 0,
        totalQuestions: Number(totalQuestions) || 0
      };
    }

    // Find the section data for the active tab
    if (finalSectionalResults && finalSectionalResults.length > 0) {
      const section = finalSectionalResults.find(s => 
        s.name.toLowerCase().replace(/\s+/g, '-') === activeTab
      );
      if (section) {
        return section;
      }
    }

    // Fallback to testConfig sections
    if (testConfig && testConfig.sections) {
      const section = testConfig.sections.find(s => s.id === activeTab);
      if (section) {
        // Create section data from testConfig
        // Calculate actual time spent on this section from detailedResults or questions
        let sectionTimeSpent = 0;
        if (results.detailedResults && Array.isArray(results.detailedResults)) {
          // Use actual time from detailedResults
          const sectionDetailedResults = results.detailedResults.filter((_, index) => 
            index >= section.startQuestion - 1 && index <= section.endQuestion - 1
          );
          sectionTimeSpent = sectionDetailedResults.reduce((sum, dr) => sum + (dr.timeSpent || 0), 0);
        } else if (results.questions && Array.isArray(results.questions)) {
          // Use actual time from questions array
          const sectionQuestionsWithTime = results.questions.filter((_, index) => 
            index >= section.startQuestion - 1 && index <= section.endQuestion - 1
          );
          sectionTimeSpent = sectionQuestionsWithTime.reduce((sum, q) => sum + (q.timeSpent || 0), 0);
        } else {
          // Fallback: Estimate time spent on this section proportionally
          sectionTimeSpent = Math.floor((timeSpent || 0) * (section.totalQuestions / totalQuestions));
        }
        
        // Calculate totalTime for section (convert minutes to seconds if needed)
        const sectionTotalTime = section.timeLimit 
          ? (section.timeLimit * 60) // Convert minutes to seconds
          : (section.totalQuestions * 60); // Default: 60 seconds per question
        
        return {
          name: section.name,
          score: Math.floor((correctAnswers / totalQuestions) * section.totalQuestions * 4),
          totalMarks: section.totalQuestions * 4,
          correctAnswers: Math.floor((correctAnswers / totalQuestions) * section.totalQuestions),
          wrongAnswers: Math.floor((wrongAnswers / totalQuestions) * section.totalQuestions),
          unattempted: section.totalQuestions - Math.floor(((correctAnswers + wrongAnswers) / totalQuestions) * section.totalQuestions),
          accuracy: accuracy,
          timeSpent: sectionTimeSpent,
          totalTime: sectionTotalTime,
          attempted: Math.floor(((correctAnswers + wrongAnswers) / totalQuestions) * section.totalQuestions),
          totalQuestions: section.totalQuestions
        };
      }
    }

    // Return overall data if section not found
    return {
      score: score,
      totalMarks: totalMarks,
      correctAnswers: correctAnswers,
      wrongAnswers: wrongAnswers,
      unattempted: unattempted,
      accuracy: accuracy,
      timeSpent: timeSpent || 0,
      totalTime: totalTime,
      attempted: attempted,
      totalQuestions: totalQuestions
    };
  };

  const currentSectionData = getCurrentSectionData();

  // Function to get section-specific comparison data
  const getSectionComparisonData = (sectionData) => {
    // If we have sectional stats, use them for comparison
    if (results.sectionalStats && results.sectionalStats.length > 0) {
      const sectionStat = results.sectionalStats.find(stat => 
        stat.name.toLowerCase().replace(/\s+/g, '-') === activeTab ||
        stat.name === sectionData.name
      );
      
      if (sectionStat) {
        return {
          topper: {
            score: Math.min(sectionStat.highestScore || sectionStat.averageScore, totalMarks),
            accuracy: Math.min(sectionStat.highestAccuracy || sectionStat.averageAccuracy, 100),
            correct: sectionStat.highestCorrect || sectionStat.averageCorrect,
            wrong: sectionStat.lowestWrong || sectionStat.averageWrong,
            time: sectionStat.fastestTime || sectionStat.averageTime
          },
          average: {
            score: sectionStat.averageScore || sectionData.score,
            accuracy: sectionStat.averageAccuracy || sectionData.accuracy,
            correct: sectionStat.averageCorrect || Math.floor(sectionData.correctAnswers),
            wrong: sectionStat.averageWrong || Math.floor(sectionData.wrongAnswers),
            time: sectionStat.averageTime || sectionData.timeSpent
          },
          median: {
            score: sectionStat.medianScore || sectionStat.averageScore || sectionData.score,
            accuracy: sectionStat.averageAccuracy || sectionData.accuracy,
            correct: sectionStat.averageCorrect || Math.floor(sectionData.correctAnswers),
            wrong: sectionStat.averageWrong || Math.floor(sectionData.wrongAnswers),
            time: sectionStat.averageTime || sectionData.timeSpent
          }
        };
      }
    }
    
    // Fallback to overall data with section-specific adjustments
    const sectionRatio = sectionData.totalQuestions / totalQuestions;
    
    // Generate realistic comparison data based on current performance
    const currentScore = sectionData.score;
    const currentAccuracy = sectionData.accuracy;
    const currentCorrect = sectionData.correctAnswers;
    const currentWrong = sectionData.wrongAnswers;
    const currentTime = sectionData.timeSpent || 0;
    
    // Use real analytics data if available, otherwise fall back to current user's data
    const realTopper = topPerformers && topPerformers.length > 0 ? topPerformers[0] : null;
    
    // Calculate topper performance using real data
    const topperScore = realTopper ? Math.min(realTopper.score, totalMarks) : sectionData.score;
    const topperAccuracy = realTopper ? realTopper.accuracy : sectionData.accuracy;
    const topperCorrect = realTopper ? realTopper.correctAnswers : sectionData.correctAnswers;
    const topperWrong = realTopper ? realTopper.wrongAnswers : sectionData.wrongAnswers;
    const topperTime = realTopper && realTopper.timeSpent > 0 ? realTopper.timeSpent : (results.timeAnalysis?.fastestCompletion || sectionData.timeSpent);
    
    // Use real average data if available
    const avgScore = averageScore > 0 ? averageScore : sectionData.score;
    const avgAccuracy = averageScore > 0 ? Math.round((averageScore / totalMarks) * 100) : sectionData.accuracy;
    const avgCorrect = averageScore > 0 ? Math.round((averageScore / 4)) : sectionData.correctAnswers; // Assuming 4 marks per question
    const avgWrong = averageScore > 0 ? Math.max(0, Math.round((totalMarks - averageScore) / 4)) : sectionData.wrongAnswers;
    
    // Use real average time from analytics if available, otherwise use current user's time
    const avgTime = results.timeAnalysis?.averageCompletionTime || timeSpent;
    
    return {
      topper: {
        score: topperScore,
        accuracy: topperAccuracy,
        correct: topperCorrect,
        wrong: topperWrong,
        time: topperTime
      },
      average: {
        score: avgScore,
        accuracy: avgAccuracy,
        correct: avgCorrect,
        wrong: avgWrong,
        time: avgTime
      },
      median: {
        score: avgScore,
        accuracy: avgAccuracy,
        correct: avgCorrect,
        wrong: avgWrong,
        time: avgTime
      }
    };
  };

  // Get section-specific comparison data
  const comparisonData = getSectionComparisonData(currentSectionData);

  // If this is the first attempt (no other data), show current user as topper
  if (totalParticipants <= 1) {
    comparisonData.topper = {
      score: currentSectionData.score,
      accuracy: currentSectionData.accuracy,
      correct: currentSectionData.correctAnswers,
      wrong: currentSectionData.wrongAnswers,
      time: currentSectionData.timeSpent || 0
    };
    comparisonData.average = { ...comparisonData.topper };
    comparisonData.median = { ...comparisonData.topper };
  }

  // Handle case when no results are available
  if (!results || !hasValidData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Award className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Results Available</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {!results ? 'Test results are not available yet.' : 'Test results data is incomplete or invalid.'}
          </p>
          <div className="text-sm text-gray-500 mb-4">
            {results && (
              <pre className="text-left bg-gray-100 p-2 rounded text-xs overflow-auto max-w-md">
                {JSON.stringify(results, null, 2)}
              </pre>
            )}
          </div>
          <button
            onClick={onBack}
            className="btn-primary"
          >
            Back to Tests
          </button>
        </div>
      </div>
    );
  }

  // Use real marks distribution from results, or create one with current user's score
  const marksDistribution = results.marksDistribution && results.marksDistribution.length > 0 
    ? results.marksDistribution 
    : (() => {
        // Get the maximum score for the current section
        const maxScore = activeTab === 'overview' ? totalMarks : currentSectionData.totalMarks;
        const currentScore = activeTab === 'overview' ? score : currentSectionData.score;
        
        // Calculate minimum possible score (negative marks possible)
        // Estimate worst case: if all questions are wrong with negative marking
        // Typical negative marking: -1 per wrong answer, so worst case ≈ -totalQuestions
        // But we'll estimate based on maxScore (assuming 4 marks per question, -1 per wrong)
        // Worst case ≈ -maxScore/4, but we'll use a more conservative -maxScore/5
        const estimatedMinScore = Math.floor(-maxScore / 5);
        const minScore = currentScore < 0 
          ? Math.min(estimatedMinScore, Math.floor(currentScore / 10) * 10 - 10)
          : estimatedMinScore;
        
        const ranges = [];
        
        // Negative ranges (2 ranges below 0) - ensure no overlap
        if (minScore < 0) {
          const negativeRangeSize = Math.max(10, Math.ceil(Math.abs(minScore) / 2));
          let negativeStart = minScore;
          for (let i = 0; i < 2; i++) {
            const start = negativeStart;
            const end = i === 1 ? -1 : Math.min(-1, start + negativeRangeSize - 1);
            if (start <= end) {
              ranges.push({
                min: start,
                max: end,
                range: `${start}-${end}`,
                students: 0
              });
              negativeStart = end + 1;
              if (negativeStart >= 0) break;
            }
          }
        }
        
        // Positive ranges (10 ranges from 0 to maxScore) - ensure no overlap
        const positiveRangeSize = Math.max(1, Math.ceil(maxScore / 10));
        for (let i = 0; i < 10; i++) {
          const start = i * positiveRangeSize;
          const end = i === 9 ? maxScore : Math.min(maxScore, (i + 1) * positiveRangeSize - 1);
          if (start <= end) {
            ranges.push({
              min: start,
              max: end,
              range: `${start}-${end}`,
              students: 0
            });
          }
        }
        
        // If we have a score, place it in the appropriate range (only once)
        if (currentScore !== undefined && currentScore !== null && !isNaN(currentScore)) {
          const numericScore = Number(currentScore);
          // Find which range the score falls into (first matching range only)
          for (let i = 0; i < ranges.length; i++) {
            const range = ranges[i];
            if (numericScore >= range.min && numericScore <= range.max) {
              ranges[i].students = 1;
              break; // Only assign to first matching range to prevent double counting
            }
          }
        }
        
        return ranges;
      })();

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds) || seconds === undefined || seconds < 0) {
      return '0:00';
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    // If hours > 0, show HH:MM:SS format
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    // Otherwise show MM:SS format
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimeFull = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }
    return `${minutes}m ${secs}s`;
  };

  // Calculate dynamic cutoff based on category and percentile
  const calculateDynamicCutoff = (category) => {
    if (!results.percentileRanges || results.percentileRanges.length === 0) {
      return cutOff; // Fallback to default cutoff
    }

    let targetPercentile;
    switch (category) {
      case 'General':
        targetPercentile = 95;
        break;
      case 'OBC':
      case 'EWS':
        targetPercentile = 93;
        break;
      case 'SC':
      case 'ST':
        targetPercentile = 90;
        break;
      default:
        targetPercentile = 95;
    }

    // Find the score at the target percentile
    const targetRange = results.percentileRanges.find(range => 
      range.percentile >= targetPercentile
    );
    
    if (targetRange) {
      return Math.round(targetRange.score);
    }

    // If exact percentile not found, interpolate
    const sortedRanges = results.percentileRanges.sort((a, b) => a.percentile - b.percentile);
    const lowerRange = sortedRanges.find(range => range.percentile <= targetPercentile);
    const upperRange = sortedRanges.find(range => range.percentile >= targetPercentile);
    
    if (lowerRange && upperRange) {
      const ratio = (targetPercentile - lowerRange.percentile) / (upperRange.percentile - lowerRange.percentile);
      return Math.round(lowerRange.score + (upperRange.score - lowerRange.score) * ratio);
    }

    return cutOff; // Fallback
  };

  const dynamicCutoff = calculateDynamicCutoff(selectedCategory);

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-100 dark:bg-green-900/20';
    if (score >= 80) return 'text-blue-600 bg-blue-100 dark:bg-blue-900/20';
    if (score >= 70) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20';
    if (score >= 60) return 'text-orange-600 bg-orange-100 dark:bg-orange-900/20';
    return 'text-red-600 bg-red-100 dark:bg-red-900/20';
  };

  const getScoreMessage = (score) => {
    if (score >= 90) return 'Excellent! Outstanding performance!';
    if (score >= 80) return 'Great job! Well done!';
    if (score >= 70) return 'Good work! Keep practicing!';
    if (score >= 60) return 'Not bad! Room for improvement.';
    return 'Keep practicing! You can do better!';
  };

  const getScoreIcon = (score) => {
    if (score >= 90) return <Trophy className="h-8 w-8 text-green-600" />;
    if (score >= 80) return <Star className="h-8 w-8 text-blue-600" />;
    if (score >= 70) return <Target className="h-8 w-8 text-yellow-600" />;
    return <AlertTriangle className="h-8 w-8 text-red-600" />;
  };

  // Capture screenshot of the results
  const captureScreenshot = async () => {
    if (!resultsContainerRef.current) {
      toast.error('Unable to capture screenshot');
      return null;
    }

    try {
      setIsCapturing(true);
      
      // Hide any tooltips or modals before capturing
      setHoveredBar(null);
      
      // Wait a bit for any animations to complete
      await new Promise(resolve => setTimeout(resolve, 300));

      // Detect dark mode
      const isDarkMode = document.documentElement.classList.contains('dark') || 
                        document.body.classList.contains('dark') ||
                        window.matchMedia('(prefers-color-scheme: dark)').matches;
      
      const backgroundColor = isDarkMode ? '#111827' : '#f9fafb'; // gray-900 or gray-50

      const dataUrl = await toPng(resultsContainerRef.current, {
        quality: 1.0,
        pixelRatio: 2,
        backgroundColor: backgroundColor,
        cacheBust: true,
        includeQueryParams: true
      });

      return dataUrl;
    } catch (error) {
      toast.error('Failed to capture screenshot');
      return null;
    } finally {
      setIsCapturing(false);
    }
  };

  // Handle share functionality
  const handleShare = async () => {
    const dataUrl = await captureScreenshot();
    if (!dataUrl) return;

    try {
      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const file = new File([blob], `${testName || 'Test Results'}_${new Date().getTime()}.png`, {
        type: 'image/png'
      });

      // Use Web Share API if available
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `${testType} Results - ${testName}`,
          text: `Check out my ${testType} results!`,
          files: [file]
        });
        toast.success('Results shared successfully!');
      } else if (navigator.clipboard && navigator.clipboard.write) {
        // Fallback: Copy image to clipboard
        await navigator.clipboard.write([
          new ClipboardItem({
            'image/png': blob
          })
        ]);
        toast.success('Screenshot copied to clipboard!');
      } else {
        // Final fallback: Download the image
        handleDownload();
      }
    } catch (error) {
      // Fallback to download if share fails
      handleDownload();
    }
  };

  // Handle download functionality
  const handleDownload = async () => {
    const dataUrl = await captureScreenshot();
    if (!dataUrl) return;

    try {
      // Create a download link
      const link = document.createElement('a');
      link.download = `${testName || 'Test Results'}_${new Date().getTime()}.png`;
      link.href = dataUrl;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Results downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download results');
    }
  };

  return (
    <div ref={resultsContainerRef} className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 px-2 sm:px-4 lg:px-8 py-3 sm:py-4 sm:h-16">
          <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
            <button
              onClick={onBack}
              className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex-shrink-0"
            >
              <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
                {testType} Results
              </h1>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">
                {testName}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-1.5 sm:space-x-3 flex-shrink-0">
            <button 
              onClick={() => setShowAnalysisModal(true)}
              className="btn-primary inline-flex items-center px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm"
            >
              <BarChart3 size={14} className="sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Analysis</span>
            </button>
            <button 
              onClick={handleShare}
              disabled={isCapturing}
              className="btn-secondary inline-flex items-center px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Share size={14} className="sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">{isCapturing ? 'Capturing...' : 'Share'}</span>
            </button>
            <button 
              onClick={handleDownload}
              disabled={isCapturing}
              className="btn-secondary inline-flex items-center px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm hidden sm:inline-flex disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={14} className="sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">{isCapturing ? 'Capturing...' : 'Download'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-2 sm:p-4 lg:p-8">
        {/* Performance Summary Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4 mb-4 sm:mb-8"
        >
          <div className="card p-2 sm:p-4 text-center">
            <div className="flex items-center justify-center mb-1 sm:mb-2">
              <Award className="h-4 w-4 sm:h-6 sm:w-6 text-red-600" />
            </div>
            <div className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white break-words">
              {rank.toLocaleString()} / {totalParticipants.toLocaleString()}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Rank</div>
          </div>

          <div className="card p-2 sm:p-4 text-center">
            <div className="flex items-center justify-center mb-1 sm:mb-2">
              <Trophy className="h-4 w-4 sm:h-6 sm:w-6 text-purple-600" />
            </div>
            <div className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white break-words">
              {activeTab === 'overview' ? score : currentSectionData.score} / {activeTab === 'overview' ? totalMarks : currentSectionData.totalMarks}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Score</div>
          </div>

          <div className="card p-2 sm:p-4 text-center">
            <div className="flex items-center justify-center mb-1 sm:mb-2">
              <BookOpen className="h-4 w-4 sm:h-6 sm:w-6 text-blue-600" />
            </div>
            <div className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white break-words">
              {currentSectionData.attempted} / {currentSectionData.totalQuestions}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Attempted</div>
          </div>

          <div className="card p-2 sm:p-4 text-center">
            <div className="flex items-center justify-center mb-1 sm:mb-2">
              <Target className="h-4 w-4 sm:h-6 sm:w-6 text-green-600" />
            </div>
            <div className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
              {currentSectionData.accuracy}%
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Accuracy</div>
          </div>

          <div className="card p-2 sm:p-4 text-center">
            <div className="flex items-center justify-center mb-1 sm:mb-2">
              <Users className="h-4 w-4 sm:h-6 sm:w-6 text-purple-600" />
            </div>
            <div className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
              {percentile}%
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Percentile</div>
          </div>
        </motion.div>

        {/* Sectional Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-3 sm:p-6 mb-4 sm:mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
              Sectional Summary
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Expected cutoff:</span>
              <select 
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 min-w-0"
              >
                <option value="General">General (95%)</option>
                <option value="OBC">OBC (93%)</option>
                <option value="EWS">EWS (93%)</option>
                <option value="SC">SC (90%)</option>
                <option value="ST">ST (90%)</option>
              </select>
              <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                {dynamicCutoff} marks
              </span>
              <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                score >= dynamicCutoff 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
                  : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
              }`}>
                {score >= dynamicCutoff ? 'Above Cutoff' : 'Below Cutoff'}
              </span>
            </div>
          </div>

          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <table className="w-full min-w-[600px] sm:min-w-0">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm text-gray-900 dark:text-white">Section</th>
                  <th className="text-center py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm text-gray-900 dark:text-white">Score</th>
                  <th className="text-center py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm text-gray-900 dark:text-white">Attempted</th>
                  <th className="text-center py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm text-gray-900 dark:text-white">Accuracy</th>
                  <th className="text-center py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm text-gray-900 dark:text-white">Time</th>
                </tr>
              </thead>
              <tbody>
                {finalSectionalResults.map((section, index) => (
                  <tr key={index} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm text-gray-900 dark:text-white">
                      {section.name}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                        <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                          {section.score} / {section.totalMarks}
                        </span>
                        <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                          <div 
                            className="h-1.5 sm:h-2 bg-purple-600 rounded-full"
                            style={{ width: `${(section.score / section.totalMarks) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                        <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                          {section.attempted} / {section.totalQuestions}
                        </span>
                        <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                          <div 
                            className="h-1.5 sm:h-2 bg-blue-400 rounded-full"
                            style={{ width: `${(section.attempted / section.totalQuestions) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                        <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                          {section.accuracy}%
                        </span>
                        <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                          <div 
                            className="h-1.5 sm:h-2 bg-green-400 rounded-full"
                            style={{ width: `${section.accuracy}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                        <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                          {formatTime(section.timeSpent)} / {formatTime(section.totalTime)}
                        </span>
                        <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                          <div 
                            className="h-1.5 sm:h-2 bg-yellow-500 rounded-full"
                            style={{ width: `${(section.timeSpent / section.totalTime) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
                {/* Overall Row */}
                <tr className="bg-gray-50 dark:bg-gray-800 font-semibold">
                  <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm text-gray-900 dark:text-white">Overall</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                      <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {score} / {totalMarks}
                      </span>
                      <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div 
                          className="h-1.5 sm:h-2 bg-purple-600 rounded-full"
                          style={{ width: `${percentageScore}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 mt-1 text-center sm:text-left">
                      {dynamicCutoff} - {dynamicCutoff + 3} cut-off
                    </div>
                  </td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                      <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {attempted} / {totalQuestions}
                      </span>
                      <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div 
                          className="h-1.5 sm:h-2 bg-blue-400 rounded-full"
                          style={{ width: `${(attempted / totalQuestions) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                      <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {accuracy}%
                      </span>
                      <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div 
                          className="h-1.5 sm:h-2 bg-green-400 rounded-full"
                          style={{ width: `${accuracy}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                      <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {formatTime(timeSpent || 0)} / {formatTime(totalTime || 0)}
                      </span>
                      <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div 
                          className="h-1.5 sm:h-2 bg-yellow-500 rounded-full"
                          style={{ width: `${((timeSpent || 0) / (totalTime || 1)) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Performance Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card p-3 sm:p-6 mb-4 sm:mb-8"
        >
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
            Compare with topper
          </h2>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 dark:border-gray-700 mb-4 sm:mb-6">
            <nav className="flex space-x-2 sm:space-x-8 overflow-x-auto scrollbar-hide -mx-3 sm:mx-0 px-3 sm:px-0">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-1 sm:space-x-2 py-2 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <tab.icon size={14} className="sm:w-4 sm:h-4" />
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Comparison Table */}
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <table className="w-full min-w-[600px] sm:min-w-0">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm text-gray-900 dark:text-white">Metric</th>
                  <th className="text-center py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm text-gray-900 dark:text-white">You</th>
                  <th className="text-center py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm text-gray-900 dark:text-white">Topper</th>
                  <th className="text-center py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm text-gray-900 dark:text-white">Avg</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm text-gray-900 dark:text-white">Score</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                      <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {currentSectionData.score} / {currentSectionData.totalMarks}
                      </span>
                      <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div 
                          className="h-1.5 sm:h-2 bg-purple-600 rounded-full"
                          style={{ width: `${(currentSectionData.score / currentSectionData.totalMarks) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                      <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {comparisonData.topper.score} / {currentSectionData.totalMarks}
                      </span>
                      <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div 
                          className="h-1.5 sm:h-2 bg-purple-600 rounded-full"
                          style={{ width: `${(comparisonData.topper.score / currentSectionData.totalMarks) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                      <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {comparisonData.average.score} / {currentSectionData.totalMarks}
                      </span>
                      <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div 
                          className="h-1.5 sm:h-2 bg-purple-600 rounded-full"
                          style={{ width: `${(comparisonData.average.score / currentSectionData.totalMarks) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm text-gray-900 dark:text-white">Accuracy</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                      <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {currentSectionData.accuracy}%
                      </span>
                      <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div 
                          className="h-1.5 sm:h-2 bg-green-500 rounded-full"
                          style={{ width: `${currentSectionData.accuracy}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                      <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {comparisonData.topper.accuracy}%
                      </span>
                      <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div 
                          className="h-1.5 sm:h-2 bg-green-500 rounded-full"
                          style={{ width: `${comparisonData.topper.accuracy}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                      <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {comparisonData.average.accuracy}%
                      </span>
                      <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div 
                          className="h-1.5 sm:h-2 bg-green-500 rounded-full"
                          style={{ width: `${comparisonData.average.accuracy}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm text-gray-900 dark:text-white">Correct</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                      <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {currentSectionData.correctAnswers} / {currentSectionData.totalQuestions}
                      </span>
                      <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div 
                          className="h-1.5 sm:h-2 bg-green-500 rounded-full"
                          style={{ width: `${(currentSectionData.correctAnswers / currentSectionData.totalQuestions) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                      <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {comparisonData.topper.correct} / {currentSectionData.totalQuestions}
                      </span>
                      <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div 
                          className="h-1.5 sm:h-2 bg-green-500 rounded-full"
                          style={{ width: `${(comparisonData.topper.correct / currentSectionData.totalQuestions) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                      <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {comparisonData.average.correct} / {currentSectionData.totalQuestions}
                      </span>
                      <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div 
                          className="h-1.5 sm:h-2 bg-green-500 rounded-full"
                          style={{ width: `${(comparisonData.average.correct / currentSectionData.totalQuestions) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm text-gray-900 dark:text-white">Wrong</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                      <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {currentSectionData.wrongAnswers} / {currentSectionData.totalQuestions}
                      </span>
                      <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div 
                          className="h-1.5 sm:h-2 bg-pink-500 rounded-full"
                          style={{ width: `${(currentSectionData.wrongAnswers / currentSectionData.totalQuestions) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                      <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {comparisonData.topper.wrong} / {currentSectionData.totalQuestions}
                      </span>
                      <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div 
                          className="h-1.5 sm:h-2 bg-pink-500 rounded-full"
                          style={{ width: `${(comparisonData.topper.wrong / currentSectionData.totalQuestions) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                      <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {comparisonData.average.wrong} / {currentSectionData.totalQuestions}
                      </span>
                      <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div 
                          className="h-1.5 sm:h-2 bg-pink-500 rounded-full"
                          style={{ width: `${(comparisonData.average.wrong / currentSectionData.totalQuestions) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm text-gray-900 dark:text-white">Time</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                      <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {formatTime(currentSectionData.timeSpent || 0)} / {formatTime(currentSectionData.totalTime || 0)}
                      </span>
                      <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div 
                          className="h-1.5 sm:h-2 bg-yellow-500 rounded-full"
                          style={{ width: `${((currentSectionData.timeSpent || 0) / (currentSectionData.totalTime || 1)) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                      <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {formatTime(comparisonData.topper.time || 0)} / {formatTime(currentSectionData.totalTime || totalTime || 0)}
                      </span>
                      <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div 
                          className="h-1.5 sm:h-2 bg-yellow-500 rounded-full"
                          style={{ width: `${((comparisonData.topper.time || 0) / (currentSectionData.totalTime || totalTime || 1)) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                      <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {formatTime(comparisonData.average.time || 0)} / {formatTime(currentSectionData.totalTime || totalTime || 0)}
                      </span>
                      <div className="w-full sm:w-16 h-1.5 sm:h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div 
                          className="h-1.5 sm:h-2 bg-yellow-500 rounded-full"
                          style={{ width: `${((comparisonData.average.time || 0) / (currentSectionData.totalTime || totalTime || 1)) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </motion.div>

                 {/* Comprehensive Analytics */}
         {results.sectionalStats && results.sectionalStats.length > 0 && (
           <motion.div
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ delay: 0.3 }}
             className="card p-6 mb-8"
           >
             <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
               Section-wise Performance Analysis
             </h2>
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
               {results.sectionalStats.map((section, index) => {
                 return (
                 <div key={index} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                   <h3 className="font-semibold text-gray-900 dark:text-white mb-3">{section.name}</h3>
                   <div className="space-y-2 text-sm">
                     <div className="flex justify-between">
                       <span className="text-gray-600 dark:text-gray-400">Avg Score:</span>
                       <span className="font-medium text-gray-900 dark:text-white">{section.averageScore}</span>
                     </div>
                     <div className="flex justify-between">
                       <span className="text-gray-600 dark:text-gray-400">Median Score:</span>
                       <span className="font-medium text-gray-900 dark:text-white">{section.medianScore || section.averageScore || 'N/A'}</span>
                     </div>
                     <div className="flex justify-between">
                       <span className="text-gray-600 dark:text-gray-400">Avg Accuracy:</span>
                       <span className="font-medium text-gray-900 dark:text-white">{section.averageAccuracy}%</span>
                     </div>
                     <div className="flex justify-between">
                       <span className="text-gray-600 dark:text-gray-400">Avg Time:</span>
                       <span className="font-medium text-gray-900 dark:text-white">{formatTime(section.averageTime || 0)}</span>
                     </div>
                     <div className="flex justify-between">
                       <span className="text-gray-600 dark:text-gray-400">Avg Correct:</span>
                       <span className="font-medium text-gray-900 dark:text-white">{section.averageCorrect}</span>
                     </div>
                     <div className="flex justify-between">
                       <span className="text-gray-600 dark:text-gray-400">Avg Wrong:</span>
                       <span className="font-medium text-gray-900 dark:text-white">{section.averageWrong}</span>
                     </div>
                   </div>
                 </div>
                 );
               })}
             </div>
           </motion.div>
         )}

         {/* Time Analysis */}
         {results.timeAnalysis && (
           <motion.div
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ delay: 0.35 }}
             className="card p-6 mb-8"
           >
             <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
               Time Analysis
             </h2>
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
               <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                 <Clock className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                 <div className="text-2xl font-bold text-blue-600">{formatTime(results.timeAnalysis?.averageTimePerQuestion || 0)}</div>
                 <div className="text-sm text-gray-600 dark:text-gray-400">Avg Time/Question</div>
               </div>
               <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                 <Zap className="h-6 w-6 text-green-600 mx-auto mb-2" />
                 <div className="text-2xl font-bold text-green-600">{formatTime(results.timeAnalysis?.fastestCompletion || 0)}</div>
                 <div className="text-sm text-gray-600 dark:text-gray-400">Fastest Completion</div>
               </div>
               <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                 <Clock className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                 <div className="text-2xl font-bold text-orange-600">{formatTime(results.timeAnalysis?.slowestCompletion || 0)}</div>
                 <div className="text-sm text-gray-600 dark:text-gray-400">Slowest Completion</div>
               </div>
               <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                 <BarChart3 className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                 <div className="text-2xl font-bold text-purple-600">{formatTime(results.timeAnalysis?.averageCompletionTime || 0)}</div>
                 <div className="text-sm text-gray-600 dark:text-gray-400">Avg Completion</div>
               </div>
             </div>
           </motion.div>
         )}

         {/* Percentile Ranges */}
         {results.percentileRanges && results.percentileRanges.length > 0 && (
           <motion.div
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ delay: 0.4 }}
             className="card p-6 mb-8"
           >
             <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
               Percentile Ranges
             </h2>
             <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
               {results.percentileRanges.map((range, index) => (
                 <div key={index} className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                   <div className="text-lg font-bold text-gray-900 dark:text-white">{range.percentile}%</div>
                   <div className="text-sm text-gray-600 dark:text-gray-400">Score: {range.score}</div>
                 </div>
               ))}
             </div>
           </motion.div>
         )}

         {/* Marks Distribution Chart */}
         <motion.div
           initial={{ opacity: 0, y: 20 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ delay: 0.45 }}
           className="card p-6 mb-8"
         >
           <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
             Marks Distribution
           </h2>
           
           {/* Y-axis label */}
           <div className="text-sm text-gray-600 dark:text-gray-400 mb-2 text-center">
             Number of students
           </div>
           
           <div className="relative">
             {/* Chart with grid lines */}
             <div 
               className="relative h-64 mb-4 px-4"
               onMouseLeave={() => setHoveredBar(null)}
             >
               {/* Grid lines */}
               <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                 {[0, 25, 50, 75, 100].map((percent) => (
                   <div key={percent} className="border-t border-gray-200 dark:border-gray-700"></div>
                 ))}
               </div>
               
               {/* Tooltip */}
               {hoveredBar !== null && marksDistribution && marksDistribution[hoveredBar] && (
                 <div
                   className="absolute z-10 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg pointer-events-none whitespace-nowrap"
                   style={{
                     left: `${tooltipPosition.x}px`,
                     top: `${tooltipPosition.y}px`,
                     transform: 'translateX(-50%)'
                   }}
                 >
                   <div className="font-semibold mb-1">
                     Range: {marksDistribution[hoveredBar].range || `${hoveredBar * 10}-${(hoveredBar + 1) * 10}`}
                   </div>
                   <div className="text-blue-300">
                     Students: {marksDistribution[hoveredBar].students || 0}
                   </div>
                 </div>
               )}
               
               {/* Bar Chart */}
               <svg className="w-full h-full absolute inset-0" viewBox="0 0 100 100" preserveAspectRatio="none">
                 {(() => {
                   // Validate marksDistribution to prevent NaN errors
                   if (!marksDistribution || marksDistribution.length === 0) {
                     return null;
                   }
                   
                   const validStudents = marksDistribution.map(d => d.students || 0);
                   const maxStudents = Math.max(...validStudents, 1); // At least 1 to avoid division by zero
                   const barWidth = 100 / marksDistribution.length;
                   const gap = barWidth * 0.1; // 10% gap between bars
                   const actualBarWidth = barWidth - gap;
                   
                   const currentScore = activeTab === 'overview' ? score : currentSectionData.score;
                   const maxScore = activeTab === 'overview' ? totalMarks : currentSectionData.totalMarks;
                   
                   // Parse range from item.range to check if user's score falls in it
                   const parseRange = (rangeStr) => {
                     const parts = rangeStr.split('-');
                     if (parts.length === 2) {
                       return {
                         min: parseInt(parts[0]),
                         max: parseInt(parts[1])
                       };
                     }
                     return { min: 0, max: 0 };
                   };
                   
                   return marksDistribution.map((item, index) => {
                     const students = item.students || 0;
                     const barHeight = maxStudents > 0 ? (students / maxStudents) * 100 : 0;
                     const x = (index * barWidth) + (gap / 2);
                     const y = 100 - barHeight;
                     
                     // Check if user's score falls in this range
                     const range = parseRange(item.range);
                     const isUserRange = currentScore >= range.min && currentScore <= range.max;
                     
                     return (
                       <g key={index}>
                         {/* Bar */}
                         <rect
                           x={x}
                           y={y}
                           width={actualBarWidth}
                           height={barHeight}
                           fill={isUserRange ? "#8B5CF6" : "#3B82F6"}
                           className="transition-all duration-300 cursor-pointer"
                           style={{
                             opacity: hoveredBar === index ? 0.8 : hoveredBar !== null ? 0.5 : 1
                           }}
                           rx="2"
                           onMouseEnter={(e) => {
                             setHoveredBar(index);
                             const svg = e.currentTarget.ownerSVGElement;
                             const container = svg?.parentElement;
                             if (container) {
                               const svgRect = svg.getBoundingClientRect();
                               const containerRect = container.getBoundingClientRect();
                               const barCenterX = ((x + actualBarWidth / 2) / 100) * svgRect.width;
                               setTooltipPosition({
                                 x: barCenterX,
                                 y: ((y - 5) / 100) * svgRect.height
                               });
                             }
                           }}
                           onMouseLeave={() => setHoveredBar(null)}
                         />
                         {/* Bar value label on top */}
                         {students > 0 && (
                           <text
                             x={x + actualBarWidth / 2}
                             y={y - 2}
                             textAnchor="middle"
                             fontSize="6"
                             fill={isUserRange ? "#8B5CF6" : "#3B82F6"}
                             fontWeight="bold"
                           >
                             {students}
                           </text>
                         )}
                         {/* User indicator line */}
                         {isUserRange && (
                           <line
                             x1={x + actualBarWidth / 2}
                             y1={y}
                             x2={x + actualBarWidth / 2}
                             y2="100"
                             stroke="#8B5CF6"
                             strokeWidth="1"
                             strokeDasharray="2,2"
                             className="animate-pulse"
                           />
                         )}
                       </g>
                     );
                   });
                 })()}
               </svg>
               
               {/* X-axis labels */}
               <div className="absolute bottom-0 left-0 right-0 flex justify-between px-4">
                 {marksDistribution && marksDistribution.length > 0 ? marksDistribution.map((item, index) => (
                   <div key={index} className="text-xs text-gray-600 dark:text-gray-400 text-center leading-tight">
                     {item.range || `${index * 10}-${(index + 1) * 10}`}
                   </div>
                 )) : (
                   // Default labels if no marks distribution
                   Array.from({ length: 10 }, (_, index) => (
                     <div key={index} className="text-xs text-gray-600 dark:text-gray-400 text-center leading-tight">
                       {index * 10}-{(index + 1) * 10}
                     </div>
                   ))
                 )}
               </div>
             </div>
             
             {/* Annotations */}
             <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                               <div className="flex justify-between items-center">
                  <span>Average: {comparisonData.average.score} | Median: {Math.round(comparisonData.median.score)}</span>
                  <span>Total Students: {totalParticipants > 0 ? totalParticipants.toLocaleString() : (
                    marksDistribution && marksDistribution.length > 0 
                      ? marksDistribution.reduce((sum, item) => sum + (item.students || 0), 0) 
                      : 1
                  ).toLocaleString()}</span>
                </div>
             </div>
           </div>
         </motion.div>

        {/* Action Buttons */}
        <div className="flex justify-center space-x-4">
          <button
            onClick={onTakeAnotherTest}
            className="btn-secondary inline-flex items-center"
          >
            <BookOpen size={16} className="mr-2" />
            Take Another Test
          </button>
          <button
            onClick={onPracticeWeakAreas}
            className="btn-primary inline-flex items-center"
          >
            <TrendingUp size={16} className="mr-2" />
            Practice Weak Areas
          </button>
        </div>
      </div>

      {/* Test Analysis Modal */}
      <TestAnalysisModal
        isOpen={showAnalysisModal}
        onClose={() => setShowAnalysisModal(false)}
        testResult={results}
      />
    </div>
  );
};

export default EnhancedTestResults;
